// The Plans tab (today's plans/PlansPage.tsx): the selected project's plans,
// grouped by status, live work first and the finished groups folded, each
// with its progress over the issues it lists, as the store has them. New plan
// makes one from today's template and opens it.
import { memo, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react'
import { flushSync } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import type { Issue, Label, Me, PlanRow, PlanStatus } from '../api/types'
import { PlanStatusIcon, UserIcon } from '../components/Icons'
import { useMounted } from '../components/layout'
import { useUnsavedChanges } from '../components/unsaved'
import { useProjectTitle } from '../components/useDocumentTitle'
import { planPath, projectPath } from '../projects/routing'
import { senderName } from '../sessions/words'
import { useBoardState, useSelectedProject, useStore } from '../store/context'
import { PLAN_STATUS_PAGE_ORDER, planStatusName } from '../vocab'
import { useWorkspaceDraft } from '../workspace/drafts'
import { ProgressBar } from './ProgressBar'
import { comparePlans, planLabels, planProgress } from './state'
import template from './template.html?raw'
import markdownTemplate from './template.md?raw'
import { pageWords as w } from './words'
import './plans.css'

/** Finished groups start folded; they grow long. */
/** The most label chips a plan shows. */
const MAX_CHIPS = 4

const FOLDED: ReadonlySet<PlanStatus> = new Set(['completed', 'cancelled'])

export function PlansPage() {
  const { plans, issues, labels, me } = useBoardState()
  const project = useSelectedProject()
  const [unfolded, setUnfolded] = useState<ReadonlySet<PlanStatus>>(new Set())
  useProjectTitle(w.title)

  const groups = useMemo(() => {
    const own = [...plans.values()].filter((p) => p.project === project?.id)
    return PLAN_STATUS_PAGE_ORDER.map((status) => ({ status, list: own.filter((p) => p.status === status).sort(comparePlans) })).filter((g) => g.list.length > 0)
  }, [plans, project?.id])

  return (
    <div className="plans-page">
      <header className="plans-head">
        <h1 className="page-title">{w.title}</h1>
        {project && <NewPlan projectId={project.id} projectKey={project.key} />}
      </header>
      {groups.length === 0 && <p className="muted">{w.noPlans}</p>}
      {groups.map(({ status, list }) => {
        const folded = FOLDED.has(status) && !unfolded.has(status)
        return (
          <section key={status} className="plan-group" aria-labelledby={`group-${status}`}>
            <header className="plan-group-header">
              <PlanStatusIcon status={status} />
              <h2 id={`group-${status}`}>{planStatusName(status)}</h2>
              <span className="section-count">{list.length}</span>
              {FOLDED.has(status) && (
                <button
                  type="button"
                  className="button button-quiet button-small"
                  aria-expanded={!folded}
                  onClick={() =>
                    setUnfolded((s) => {
                      const next = new Set(s)
                      if (next.has(status)) next.delete(status)
                      else next.add(status)
                      return next
                    })
                  }
                >
                  {folded ? w.show : w.hide}
                </button>
              )}
            </header>
            {!folded && (
              <ul className="plan-list">
                {list.map((plan) => (
                  <PlanItem key={plan.id} plan={plan} issues={issues} labels={labels} me={me} projectKey={project?.key ?? ''} />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

const PlanItem = memo(function PlanItem({ plan, issues, labels, me, projectKey }: { plan: PlanRow; issues: ReadonlyMap<string, Issue>; labels: ReadonlyMap<string, Label>; me: Me | null; projectKey: string }) {
  const progress = planProgress(plan, issues)
  const chips = planLabels(plan, issues, labels).slice(0, MAX_CHIPS)
  const claimer = plan.claim ? senderName(plan.claim.by, me) : null
  return (
    <li className="plan-item">
      <div className="plan-item-main">
        <Link to={planPath(projectKey, plan.id)} className="plan-item-title">
          {plan.title}
        </Link>
        {plan.summary && <p className="plan-item-summary">{plan.summary}</p>}
      </div>
      <div className="plan-item-meta">
        <ProgressBar progress={progress} label={w.listedIssues} />
        {chips.map((label) => (
          <span key={label.id} className="label-chip" title={label.name}>
            <span className="label-dot" style={{ background: label.colour }} />
            <span className="label-name">{label.name}</span>
          </span>
        ))}
        {claimer && (
          <span className="plan-item-claim" title={w.claimedBy(claimer)}>
            <UserIcon size={12} />
            {claimer}
          </span>
        )}
        <Link to={`${projectPath(projectKey, '/board')}?plan=${encodeURIComponent(plan.id)}`} className="button button-quiet button-small">
          {w.board}
        </Link>
      </div>
    </li>
  )
})

const titleOrNone = (value: unknown): value is string | null => value === null || typeof value === 'string'

/** New plan: a title, then a plan made from today's template, which opens. */
function NewPlan({ projectId, projectKey }: { projectId: string; projectKey: string }) {
  const store = useStore()
  const navigate = useNavigate()
  const [title, setTitle] = useWorkspaceDraft<string | null>('plans.newPlan', null, titleOrNone, (value) => value !== null && value.trim() !== '')
  const [saving, setSaving] = useState(false)
  useUnsavedChanges(title !== null && title.trim() !== '' && !saving)
  const mounted = useMounted()

  if (title === null) {
    return (
      <button type="button" className="button button-small button-primary" onClick={() => setTitle('')}>
        {w.newPlan}
      </button>
    )
  }

  const blank = title.trim() === ''
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (saving || blank) return
    setSaving(true)
    const plan = await store.createPlan(projectId, title, store.getState().me?.plan_format === 'markdown' ? markdownTemplate : template)
    if (!mounted.current) return
    setSaving(false)
    if (plan) {
      flushSync(() => setTitle(null))
      navigate(planPath(projectKey, plan.id))
    }
  }
  const keys = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !saving) {
      event.preventDefault()
      setTitle(null)
    }
  }
  return (
    <form className="new-issue new-plan" aria-label={w.newPlan} onSubmit={(event) => void submit(event)}>
      <input className="input" aria-label={w.planTitle} placeholder={w.titlePlaceholder} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={keys} disabled={saving} autoFocus />
      <button type="button" className="button button-small" onClick={() => setTitle(null)} disabled={saving}>
        {w.cancel}
      </button>
      <button type="submit" className="button button-small button-primary" disabled={saving || blank}>
        {saving ? w.saving : w.save}
      </button>
    </form>
  )
}
